import React, { useEffect, useState, useCallback } from 'react';
import axiosInstance from '../api/axiosInstance';
import { useParams, Link } from 'react-router-dom';
import {
  ShoppingCart, Heart, Star, ArrowLeft,
  Package, Award, CheckCircle, Send
} from 'lucide-react';
import { getProductById, getProductReviews } from '../api/productAPI';
import useAuthStore from '../store/authStore';
import useCartStore from '../store/cartStore';
import { addToWishList } from '../api/userAPI';
import toast from 'react-hot-toast';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useStoreSettings } from '../context/StoreSettingsContext';
import './ProductPage.css';

const ProductPage = () => {
  const { id } = useParams();
  const { token } = useAuthStore();
  const { addToCart } = useCartStore();
  const { settings, showPrice, canAddToCart, isLoggedIn } = useStoreSettings();

  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(false);
  const [images, setImages] = useState([]);
  const [variations, setVariations] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [meatVariants, setMeatVariants] = useState([]);
  const [selectedMeatVariant, setSelectedMeatVariant] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [userOrders, setUserOrders] = useState([]);
  const [hasOrdered, setHasOrdered] = useState(false);

  const fetchProduct = useCallback(async () => {
    setLoading(true);
    try {
      const productRes = await getProductById(id);
      if (productRes.success) {
        setProduct(productRes.data);
      } else {
        toast.error('Product not found');
        setLoading(false);
        return;
      }
      try {
        const reviewsRes = await getProductReviews(id);
        if (reviewsRes.success) setReviews(reviewsRes.data);
      } catch (e) {}
      try {
        const imagesRes = await axiosInstance.get(`/products/${id}/images`);
        if (imagesRes.data.success) setImages(imagesRes.data.data);
      } catch (e) {}
      try {
        const variationsRes = await axiosInstance.get(`/products/${id}/variations`);
        if (variationsRes.data.success) setVariations(variationsRes.data.data);
      } catch (e) {}
      try {
        const meatRes = await axiosInstance.get(`/products/${id}/variants`);
        if (meatRes.data.success) setMeatVariants(meatRes.data.data);
      } catch (e) {}
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProduct();
  }, [id, fetchProduct]);

  useEffect(() => {
    if (isLoggedIn && product) {
      fetchUserOrdersForProduct();
    }
  }, [isLoggedIn, product]);

  useEffect(() => {
    if (selectedMeatVariant?.imageUrl) {
      setSelectedImage(selectedMeatVariant.imageUrl);
    }
  }, [selectedMeatVariant]);

const fetchUserOrdersForProduct = async () => {
  try {
    const res = await axiosInstance.get('/orders');
    if (res.data.success) {
      const orders = res.data.data;
      console.log('Sample order:', JSON.stringify(orders[0]));
      
      const deliveredOrders = orders.filter(o => o.status === 'DELIVERED');
      
      for (const order of deliveredOrders) {
        // Try direct items check first
        const found = order.items?.some(item =>
          String(item.product?.id) === String(id) ||
          String(item.productId) === String(id)
        );
        
        if (found) {
          setHasOrdered(true);
          setUserOrders([order]);
          return;
        }
        
        // If items empty/missing, fetch order detail
        try {
          const detail = await axiosInstance.get(`/orders/${order.id}`);
          if (detail.data.success) {
            const detailItems = detail.data.data.items || [];
            const foundInDetail = detailItems.some(item =>
              String(item.product?.id) === String(id) ||
              String(item.productId) === String(id)
            );
            if (foundInDetail) {
              setHasOrdered(true);
              setUserOrders([order]);
              return;
            }
          }
        } catch (e) {}
      }
    }
  } catch (e) {}
};

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!isLoggedIn) {
      window.location.href = 'https://app.auroragh.com/login';
      return;
    }
    if (userOrders.length === 0) return;
    setSubmittingReview(true);
    try {
      const res = await axiosInstance.post(
        `/reviews?productId=${id}&orderId=${userOrders[0].id}&rating=${reviewForm.rating}&comment=${encodeURIComponent(reviewForm.comment)}`
      );
      if (res.data.success) {
        toast.success('Review submitted! It will appear after approval.');
        setReviewForm({ rating: 5, comment: '' });
        fetchProduct();
      } else {
        toast.error(res.data.message || 'Failed to submit review');
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleAddToCart = async () => {
    if (!token) {
      toast.error('Please login to add items to cart');
      return;
    }
    if (meatVariants.length > 0 && !selectedMeatVariant) {
      toast.error('Please select a piece or box first');
      return;
    }
    setAddingToCart(true);
    await addToCart(product.id, quantity, selectedMeatVariant?.id);
    setAddingToCart(false);
  };

  const handleAddToWishlist = async () => {
    if (!token) {
      toast.error('Please login to add items to wishlist');
      return;
    }
    try {
      await addToWishList(product.id);
      toast.success('Added to wishlist');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add to wishlist');
    }
  };

  const getClassBadge = (productClass) => {
    switch (productClass) {
      case 'FIRST': return { label: 'First Class', color: '#29ABE2' };
      case 'BUSINESS': return { label: 'Business', color: '#1565C0' };
      default: return { label: 'Economy', color: '#64748B' };
    }
  };

  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="product-page-loading">
          <div className="product-skeleton-detail" />
        </div>
        <Footer />
      </>
    );
  }

  if (!product) {
    return (
      <>
        <Navbar />
        <div className="product-not-found">
          <Package size={64} color="#29ABE2" strokeWidth={1} />
          <h2>Product not found</h2>
          <Link to="/shop" className="btn-primary">Back to Shop</Link>
        </div>
        <Footer />
      </>
    );
  }

  const classBadge = getClassBadge(product.productClass);

  return (
    <>
      <Navbar />
      <div className="product-page">
        <div className="container">

          {/* Breadcrumb */}
          <div className="breadcrumb">
            <Link to="/shop">
              <ArrowLeft size={16} /> Back to Shop
            </Link>
            <span>/</span>
            <span>{product.category?.name}</span>
            <span>/</span>
            <span>{product.name}</span>
          </div>

          {/* Product Detail */}
          <div className="product-detail">

            {/* Gallery */}
            <div className="product-gallery">
              <div className="product-main-image">
                {selectedImage || product.imageUrl ? (
                  <img src={selectedImage || product.imageUrl} alt={product.name} />
                ) : (
                  <div className="product-detail-placeholder">
                    <Package size={80} color="#29ABE2" strokeWidth={1} />
                  </div>
                )}
              </div>
              {images.length > 0 && (
                <div className="product-thumbnails">
                  {product.imageUrl && (
                    <button
                      className={`thumbnail ${!selectedImage ? 'thumbnail-active' : ''}`}
                      onClick={() => setSelectedImage(null)}
                    >
                      <img src={product.imageUrl} alt="Main" />
                    </button>
                  )}
                  {images.map((img) => (
                    <button
                      key={img.id}
                      className={`thumbnail ${selectedImage === img.imageUrl ? 'thumbnail-active' : ''}`}
                      onClick={() => setSelectedImage(img.imageUrl)}
                    >
                      <img src={img.imageUrl} alt={product.name} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="product-detail-info">
              <div className="product-detail-badges">
                <span
                  className="class-badge-detail"
                  style={{
                    color: classBadge.color,
                    background: `${classBadge.color}18`,
                    border: `1px solid ${classBadge.color}40`,
                  }}
                >
                  <Award size={12} />
                  {classBadge.label}
                </span>
                <span className="category-badge">{product.category?.name}</span>
              </div>

              <h1 className="product-detail-name">{product.name}</h1>

              {averageRating && (
                <div className="product-rating">
                  <div className="stars">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        size={16}
                        fill={star <= Math.round(averageRating) ? '#F59E0B' : 'none'}
                        color={star <= Math.round(averageRating) ? '#F59E0B' : '#CBD5E1'}
                      />
                    ))}
                  </div>
                  <span className="rating-count">
                    {averageRating} ({reviews.length} review{reviews.length !== 1 ? 's' : ''})
                  </span>
                </div>
              )}

              {/* Price */}
              <div className="product-detail-price">
                {showPrice ? (
                  <>
                    <span className="price-currency">
                      {settings.currencySymbol || 'GHS'}
                    </span>
                    <span className="price-amount">
                      {meatVariants.length > 0
                        ? selectedMeatVariant
                          ? (showPrice ? `${selectedMeatVariant.price?.toFixed(2)}` : "Login to see price")
                          : null
                        : (selectedVariation?.price || product.price)?.toFixed(2)
                      }
                    </span>
                    {meatVariants.length > 0 && !selectedMeatVariant && (
                      <span className="price-select-prompt">Select a piece or box to see price</span>
                    )}
                  </>
                ) : (
                  <a href="https://app.auroragh.com/login" className="login-to-see-price-detail">
                    Login to see price
                  </a>
                )}
              </div>

              <p className="product-detail-description">{product.description}</p>

              <div className="product-detail-perks">
                <div className="perk-item">
                  <CheckCircle size={16} color="#10B981" />
                  <span>Earn {Math.floor(product.price)} Aurora Points</span>
                </div>
                <div className="perk-item">
                  <CheckCircle size={16} color="#10B981" />
                  <span>Fresh delivery every Friday</span>
                </div>
                <div className="perk-item">
                  <CheckCircle size={16} color="#10B981" />
                  <span>Frozen delivery within 3 days</span>
                </div>
              </div>

              {/* Stock */}
              <div className="stock-info">
                {product.stockQuantity > 0 ? (
                  <span className="in-stock">
                    <CheckCircle size={14} />
                    In Stock ({product.stockQuantity} available)
                  </span>
                ) : (
                  <span className="out-of-stock">Out of Stock</span>
                )}
              </div>

              {/* Quantity */}
              {product.stockQuantity > 0 && (
                <div className="quantity-selector">
                  <label>Quantity</label>
                  <div className="quantity-controls">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                    >-</button>
                    <span>{quantity}</span>
                    <button
                      onClick={() => setQuantity(Math.min(product.stockQuantity, quantity + 1))}
                      disabled={quantity >= product.stockQuantity}
                    >+</button>
                  </div>
                </div>
              )}

              {/* Variations (legacy) — hidden once meat variants exist for this product */}
              {variations.length > 0 && meatVariants.length === 0 && (
                <div className="product-variations">
                  <label>Select Option</label>
                  <div className="variations-grid">
                    {variations.map((variation) => (
                      <button
                        key={variation.id}
                        className={`variation-btn ${
                          selectedVariation?.id === variation.id ? 'variation-btn-active' : ''
                        } ${variation.stockQuantity === 0 ? 'variation-btn-disabled' : ''}`}
                        onClick={() => setSelectedVariation(variation)}
                        disabled={variation.stockQuantity === 0}
                      >
                        <span className="variation-name">{variation.variationName}:</span>
                        <span className="variation-value">{variation.variationValue}</span>
                        {showPrice ? (
                          <span className="variation-price">
                            {settings.currencySymbol || 'GHS'} {variation.price?.toFixed(2)}
                          </span>
                        ) : (
                          <span className="variation-price-hidden">Login to see</span>
                        )}
                        {variation.stockQuantity === 0 && (
                          <span className="variation-out">Out of stock</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Meat Variant Selector (Premium Meat category) */}
              {meatVariants.length > 0 && (
                <div className="meat-variant-section">
                  <div className="meat-variant-header">
                    <span className="meat-variant-title">🥩 Select Your Piece or Box</span>
                    <span className="meat-variant-subtitle">
                      Each item has a unique weight — what you select is exactly what you receive
                    </span>
                  </div>

                  {/* Pieces */}
                  {meatVariants.filter(v => v.variantType === 'PIECE' && v.stockQuantity > 0).length > 0 && (
                    <div className="meat-variant-group">
                      <p className="meat-variant-group-label">🥩 Individual Pieces</p>
                      <div className="meat-variant-grid">
                        {meatVariants.filter(v => v.variantType === 'PIECE').map(v => (
                          <button
                            key={v.id}
                            className={`meat-variant-card ${selectedMeatVariant?.id === v.id ? 'meat-variant-card--selected' : ''} ${v.stockQuantity === 0 ? 'meat-variant-card--sold' : ''}`}
                            onClick={() => v.stockQuantity > 0 && setSelectedMeatVariant(v)}
                            disabled={v.stockQuantity === 0}
                          >
                            {v.imageUrl && <img src={v.imageUrl} alt={v.variationName} className="mvc-img" />}
                            <span className="mvc-weight">{v.weightKg ? `${v.weightKg} kg` : v.variationValue}</span>
                            <span className="mvc-name">{v.variationName}</span>
                            <span className="mvc-price">{showPrice ? `GHS ${v.price?.toFixed(2)}` : "Login to see price"}</span>
                            {v.stockQuantity === 0 && <span className="mvc-sold">Sold</span>}
                            {selectedMeatVariant?.id === v.id && v.stockQuantity > 0 && <span className="mvc-check">✓</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Boxes */}
                  {meatVariants.filter(v => v.variantType === 'BOX' && v.stockQuantity > 0).length > 0 && (
                    <div className="meat-variant-group">
                      <p className="meat-variant-group-label">📦 Full Boxes</p>
                      <div className="meat-variant-grid">
                        {meatVariants.filter(v => v.variantType === 'BOX').map(v => (
                          <button
                            key={v.id}
                            className={`meat-variant-card meat-variant-card--box ${selectedMeatVariant?.id === v.id ? 'meat-variant-card--selected' : ''} ${v.stockQuantity === 0 ? 'meat-variant-card--sold' : ''}`}
                            onClick={() => v.stockQuantity > 0 && setSelectedMeatVariant(v)}
                            disabled={v.stockQuantity === 0}
                          >
                            {v.imageUrl && <img src={v.imageUrl} alt={v.variationName} className="mvc-img" />}
                            <span className="mvc-weight">{v.weightKg ? `${v.weightKg} kg` : v.variationValue}</span>
                            <span className="mvc-name">{v.variationName}</span>
                            <span className="mvc-price">{showPrice ? `GHS ${v.price?.toFixed(2)}` : "Login to see price"}</span>
                            {v.stockQuantity === 0 && <span className="mvc-sold">Sold</span>}
                            {selectedMeatVariant?.id === v.id && v.stockQuantity > 0 && <span className="mvc-check">✓</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedMeatVariant && (
                    <div className="meat-variant-selected-summary">
                      <span>Selected: <strong>{selectedMeatVariant.variationName}</strong></span>
                      <span>{selectedMeatVariant.weightKg ? `${selectedMeatVariant.weightKg} kg` : selectedMeatVariant.variationValue}</span>
                      <span className="mvss-price">{showPrice ? `GHS ${selectedMeatVariant.price?.toFixed(2)}` : "Login to see price"}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="product-detail-actions">
                {canAddToCart ? (
                  <button
                    className="add-to-cart-detail-btn"
                    onClick={handleAddToCart}
                    disabled={product.stockQuantity === 0 || addingToCart}
                  >
                    <ShoppingCart size={18} />
                    {addingToCart ? 'Adding...' : 'Add to Cart'}
                  </button>
                ) : (
                  <a href="https://app.auroragh.com/login" className="add-to-cart-detail-btn">
                    <ShoppingCart size={18} />
                    Login to Add to Cart
                  </a>
                )}
                <button className="wishlist-detail-btn" onClick={handleAddToWishlist}>
                  <Heart size={18} />
                </button>
              </div>

              {/* Points Info */}
              {showPrice && (
                <div className="points-info">
                  <Award size={14} color="#1565C0" />
                  <span>
                    You'll earn <strong>
                      {Math.floor((selectedMeatVariant?.price || selectedVariation?.price || product.price) * quantity)} Aurora Points
                    </strong> on this purchase
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* EU Regulations */}
          {product.regulationsInfo && (
            <div className="regulations-section">
              <h3 className="regulations-title">Product Regulations & Compliance</h3>
              <div className="regulations-content">
                <p>{product.regulationsInfo}</p>
              </div>
            </div>
          )}

          {/* Reviews */}
<div className="reviews-section">
  <h2 className="reviews-title">
    Customer Reviews
    {reviews.length > 0 && (
      <span className="reviews-count">({reviews.length})</span>
    )}
  </h2>

  {/* Write a Review */}
  {isLoggedIn && hasOrdered && (
    <div className="review-form-card">
      <h3 className="review-form-title">Write a Review</h3>
      <form onSubmit={handleSubmitReview} className="review-form">
        <div className="review-rating-select">
          <label>Your Rating *</label>
          <div className="star-select">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setReviewForm({ ...reviewForm, rating: star })}
              >
                <Star
                  size={28}
                  fill={star <= reviewForm.rating ? '#F59E0B' : 'none'}
                  color={star <= reviewForm.rating ? '#F59E0B' : '#CBD5E1'}
                />
              </button>
            ))}
            <span className="rating-label">
              {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][reviewForm.rating]}
            </span>
          </div>
        </div>
        <div className="review-comment-field">
          <label>Your Review (optional)</label>
          <textarea
            rows={4}
            placeholder="Share your experience with this product..."
            value={reviewForm.comment}
            onChange={e => setReviewForm({ ...reviewForm, comment: e.target.value })}
          />
        </div>
        <button type="submit" className="review-submit-btn" disabled={submittingReview}>
          <Send size={15} />
          {submittingReview ? 'Submitting...' : 'Submit Review'}
        </button>
      </form>
    </div>
  )}

  {isLoggedIn && !hasOrdered && (
    <div className="review-no-order">
      <Star size={20} color="#CBD5E1" />
      <p>You need to purchase and receive this product to leave a review.</p>
    </div>
  )}

  {!isLoggedIn && (
    <div className="review-no-order">
      <Star size={20} color="#CBD5E1" />
      <a href="https://app.auroragh.com/login">Login</a>
      <p>to leave a review.</p>
    </div>
  )}

  {reviews.length > 0 ? (
    <div className="reviews-grid">
      {reviews.map((review) => (
        <div key={review.id} className="review-card">
          <div className="review-header">
            <div className="reviewer-info">
              <div className="reviewer-avatar">
                {review.user?.firstName?.charAt(0)}
                {review.user?.lastName?.charAt(0)}
              </div>
              <div>
                <p className="reviewer-name">
                  {review.user?.firstName} {review.user?.lastName}
                </p>
                <p className="review-date">
                  {new Date(review.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="review-stars">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  size={14}
                  fill={star <= review.rating ? '#F59E0B' : 'none'}
                  color={star <= review.rating ? '#F59E0B' : '#CBD5E1'}
                />
              ))}
            </div>
          </div>
          {review.comment && (
            <p className="review-comment">{review.comment}</p>
          )}
        </div>
      ))}
    </div>
  ) : (
    <div className="no-reviews">
      <Star size={32} color="#CBD5E1" />
      <p>No reviews yet. Be the first to review this product!</p>
    </div>
  )}
</div>

        </div>
      </div>
      <Footer />
    </>
  );
};

export default ProductPage;